/** TanStack Query hooks over the CIGS API. One query key namespace: ['cigs', ...]. */
import { type QueryClient, useMutation, useQuery, useQueryClient, type UseMutationOptions, type UseQueryOptions } from '@tanstack/react-query';
import { apiFetch, newCorrelationId } from './client';
import { endpoints } from './endpoints';
import { appendAsOf, freezePermissions, freezeRows, pathWithAsOf, readAsOfMeta } from '@/lib/as-of';
import {
  normalizeDepartment,
  normalizeDetail,
  normalizeHistory,
  normalizeList,
  normalizeMe,
  normalizeSchema,
  normalizeView,
} from './normalize';
import type {
  AttrCell,
  ChangeLogEntry,
  DepartmentTab,
  EntityDetail,
  EntityListResult,
  Me,
  SchemaDto,
  ViewDef,
} from './types';

export interface ColumnFilter {
  attrCode: string;
  /** option codes / ids for selects, [min,max] for numbers, string for text, boolean for flags */
  value: unknown;
}

export interface RegistryQuery {
  entityType: string;
  /** entity id of the department; the registry filters on the IN_SCOPE_FOR edge */
  departmentId?: string | null;
  search?: string;
  filters?: ColumnFilter[];
  sort?: Array<{ id: string; desc: boolean }>;
  hotListOnly?: boolean;
  page?: number;
  pageSize?: number;
  /** #409: a past UTC day to read as of; absent or null is now. Part of the query key. */
  asOf?: string | null;
}

/**
 * L2's list contract (api/src/registry/filter-model.ts): `type`, `q`, repeatable
 * `filter=<field>:<op>:<value>` where field is `attr:<code>`, `edge:<rel_code>`, `key`, `name`
 * or `description`, repeatable `sort=<field>:<dir>`, plus `page`/`pageSize` (max 500).
 */
export function registrySearchParams(q: RegistryQuery): string {
  const p = new URLSearchParams();
  p.set('type', q.entityType);
  if (q.search) p.set('q', q.search);
  if (q.departmentId) p.append('filter', `edge:IN_SCOPE_FOR:eq:${q.departmentId}`);
  if (q.hotListOnly) p.append('filter', 'attr:hot_list:eq:true');
  for (const f of q.filters ?? []) {
    if (Array.isArray(f.value)) p.append('filter', `attr:${f.attrCode}:in:${f.value.join(',')}`);
    else if (typeof f.value === 'boolean') p.append('filter', `attr:${f.attrCode}:eq:${f.value}`);
    else if (f.value !== null && f.value !== undefined) p.append('filter', `attr:${f.attrCode}:contains:${String(f.value)}`);
  }
  for (const s of q.sort ?? []) p.append('sort', `${s.id}:${s.desc ? 'desc' : 'asc'}`);
  p.set('page', String(q.page ?? 1));
  p.set('pageSize', String(Math.min(q.pageSize ?? 500, 500)));
  appendAsOf(p, q.asOf);
  return p.toString();
}

export const qk = {
  me: ['cigs', 'me'] as const,
  schema: (t: string) => ['cigs', 'schema', t] as const,
  departments: ['cigs', 'departments'] as const,
  views: (t: string) => ['cigs', 'views', t] as const,
  registry: (q: RegistryQuery) => ['cigs', 'registry', q] as const,
  entity: (id: string) => ['cigs', 'entity', id] as const,
  /** #409: a historical read sits under the record's key, so invalidating the record covers it. */
  entityAsOf: (id: string, asOf: string) => ['cigs', 'entity', id, { asOf }] as const,
  history: (id: string) => ['cigs', 'history', id] as const,
};

export function useMe(enabled = true) {
  return useQuery<Me>({
    queryKey: qk.me,
    enabled,
    retry: false,
    queryFn: async () => normalizeMe(await apiFetch<Record<string, unknown>>(endpoints.me)),
  });
}

export function useSchema(entityType: string, options?: Partial<UseQueryOptions<SchemaDto>>) {
  return useQuery<SchemaDto>({
    queryKey: qk.schema(entityType),
    staleTime: 5 * 60_000,
    queryFn: async () => normalizeSchema(await apiFetch<Record<string, unknown>>(endpoints.schema), entityType),
    ...options,
  });
}

/**
 * Departments are entities, so they come from the same list route. Who may edit which
 * department is decided from the caller's DeptEditor scopes — and re-checked by the server on
 * every write.
 */
export function useDepartments(me?: Me) {
  return useQuery<DepartmentTab[]>({
    queryKey: [...qk.departments, me?.id ?? 'anon'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const raw = await apiFetch<never>(`${endpoints.entities}?${registrySearchParams({ entityType: 'department', pageSize: 200 })}`);
      const list = normalizeList(raw);
      const scopes = new Set((me?.roles ?? []).filter((r) => r.role === 'DeptEditor').map((r) => r.scopeEntityId));
      return list.rows
        .map((row) =>
          normalizeDepartment({
            id: row.id,
            key: row.key,
            name: row.name,
            canEdit: Boolean(me?.isHeadOfIT) || scopes.has(row.id),
          }),
        )
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

export function useViews(entityType: string) {
  return useQuery<ViewDef[]>({
    queryKey: qk.views(entityType),
    staleTime: 60_000,
    queryFn: async () => {
      const raw = await apiFetch<unknown>(endpoints.views(entityType));
      const list = Array.isArray(raw) ? raw : ((raw as { rows?: unknown[]; views?: unknown[] }).rows ?? (raw as { views?: unknown[] }).views ?? []);
      return (list as Record<string, unknown>[]).map(normalizeView);
    },
  });
}

/**
 * B8: whether `prev` (the last successful `useRegistry` page) is safe to show as a placeholder
 * while a new query is in flight. `qk.registry(q)` already carries `q.entityType`, but
 * `placeholderData: (prev) => prev` ignored that and always reused it — capability rows kept
 * rendering under initiative columns (and a click opened the wrong type of record) for however
 * long the new fetch took. Exported so this is testable without mounting a `QueryClient`.
 */
export function registryPlaceholderData(
  prev: EntityListResult | undefined,
  previousEntityType: string | undefined,
  nextEntityType: string,
  previousAsOf: string | null = null,
  nextAsOf: string | null = null,
): EntityListResult | undefined {
  // #409: rows from another day are never a placeholder — they would show (and seed editors with)
  // values that were not true at the day now asked for.
  return previousEntityType === nextEntityType && previousAsOf === nextAsOf ? prev : undefined;
}

export function useRegistry(q: RegistryQuery, enabled = true) {
  return useQuery<EntityListResult>({
    queryKey: qk.registry(q),
    enabled,
    placeholderData: (prev, previousQuery) =>
      registryPlaceholderData(
        prev,
        (previousQuery?.queryKey[2] as RegistryQuery | undefined)?.entityType,
        q.entityType,
        (previousQuery?.queryKey[2] as RegistryQuery | undefined)?.asOf ?? null,
        q.asOf ?? null,
      ),
    queryFn: async () => {
      const raw = await apiFetch<never>(`${endpoints.entities}?${registrySearchParams(q)}`);
      const list = normalizeList(raw);
      const earliest = readAsOfMeta(raw).earliest;
      return { ...list, rows: freezeRows(q.asOf, list.rows), ...(earliest ? { earliest } : {}) };
    },
  });
}

/** #409: `asOf` (a past UTC day) reads the record as it stood then, with editing switched off. */
export function useEntity(id: string | null, asOf?: string | null) {
  return useQuery<EntityDetail>({
    queryKey: asOf ? qk.entityAsOf(id ?? 'none', asOf) : qk.entity(id ?? 'none'),
    enabled: Boolean(id),
    queryFn: async () => {
      const detail = normalizeDetail(await apiFetch<Record<string, unknown>>(pathWithAsOf(endpoints.entity(id!), asOf)));
      return asOf ? { ...detail, permissions: freezePermissions(asOf, detail.permissions) } : detail;
    },
  });
}

/** #450: the history read, with the view's as-of day when historical (unchanged for now). */
export function historyPath(id: string, asOf?: string | null): string {
  return pathWithAsOf(endpoints.entityHistory(id), asOf);
}

export function historyKey(id: string, asOf?: string | null) {
  return asOf ? ([...qk.history(id), asOf] as const) : qk.history(id);
}

export function useEntityHistory(id: string | null, enabled = true, asOf?: string | null) {
  return useQuery<ChangeLogEntry[]>({
    queryKey: historyKey(id ?? 'none', asOf),
    enabled: Boolean(id) && enabled,
    queryFn: async () => {
      const raw = await apiFetch<unknown>(historyPath(id!, asOf));
      const list = Array.isArray(raw)
        ? raw
        : ((raw as { entries?: unknown[]; rows?: unknown[] }).entries ?? (raw as { rows?: unknown[] }).rows ?? []);
      return (list as Record<string, unknown>[]).map(normalizeHistory);
    },
  });
}

export interface SetAttributeInput {
  entityId: string;
  attrCode: string;
  /** null clears the value (back to Unassessed) */
  value: string | number | boolean | string[] | null;
  optionId?: string | null;
  reason?: string;
}

/**
 * Optimistic attribute write. Patches every cached registry page and the open drawer, then
 * rolls back on failure — the server is the authority (it may refuse with 403).
 */
/** The one attribute write: PATCH /api/entities/{id}/attributes/{code} with the value and reason. */
export function putAttribute(input: SetAttributeInput): Promise<unknown> {
  return apiFetch<unknown>(endpoints.entityAttribute(input.entityId, input.attrCode), {
    method: 'PATCH',
    correlationId: newCorrelationId(),
    body: { value: input.value, reason: input.reason },
  });
}

/** R12 PR #61: an attribute whose write also changes what an obligation's Lifecycle card reads. */
const LIFECYCLE_ATTRS: ReadonlySet<string> = new Set(['fulfillment_status']);

export function useSetAttribute() {
  return useMutation(setAttributeOptions(useQueryClient()));
}

/** The attribute write's mutation options (exported so its settle order is testable). */
type SetAttributeContext = { snapshot: [readonly unknown[], EntityListResult | undefined][]; detail: EntityDetail | undefined };
export function setAttributeOptions(
  qc: QueryClient,
  mutationFn: (input: SetAttributeInput) => Promise<unknown> = putAttribute,
): UseMutationOptions<unknown, Error, SetAttributeInput, SetAttributeContext> {
  return {
    mutationFn,
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: ['cigs', 'registry'] });
      await qc.cancelQueries({ queryKey: qk.entity(input.entityId) });
      const snapshot = qc.getQueriesData<EntityListResult>({ queryKey: ['cigs', 'registry'] });
      const detail = qc.getQueryData<EntityDetail>(qk.entity(input.entityId));
      const optimistic: AttrCell = {
        value: Array.isArray(input.value) ? null : input.value,
        optionId: input.optionId ?? null,
        optionCodes: Array.isArray(input.value) ? input.value : null,
        provenance: { source: 'ui', at: new Date().toISOString(), by: 'you' },
      };

      for (const [key, data] of snapshot) {
        // #409: a historical page is what was true then — a write today never patches it.
        if (!data || (key[2] as RegistryQuery | undefined)?.asOf) continue;
        qc.setQueryData(key, {
          ...data,
          rows: data.rows.map((r) => (r.id === input.entityId ? { ...r, attrs: { ...r.attrs, [input.attrCode]: optimistic } } : r)),
        });
      }
      if (detail) {
        qc.setQueryData(qk.entity(input.entityId), {
          ...detail,
          attrs: { ...detail.attrs, [input.attrCode]: optimistic },
        });
      }
      return { snapshot, detail };
    },
    onError: (_err, input, ctx) => {
      for (const [key, data] of ctx?.snapshot ?? []) qc.setQueryData(key, data);
      if (ctx?.detail) qc.setQueryData(qk.entity(input.entityId), ctx.detail);
    },
    onSettled: (_data, _err, input) => {
      void qc.invalidateQueries({ queryKey: ['cigs', 'registry'] });
      void qc.invalidateQueries({ queryKey: qk.entity(input.entityId) });
      void qc.invalidateQueries({ queryKey: ['cigs', 'record-state'] });
      void qc.invalidateQueries({ queryKey: qk.history(input.entityId) });
      // the Lifecycle card reads again once the write has settled, never before (PR #61 round 10)
      if (LIFECYCLE_ATTRS.has(input.attrCode)) void qc.invalidateQueries({ queryKey: ['cigs', 'obligation', input.entityId] });
    },
  };
}

export interface SaveViewInput {
  id?: string;
  code: string;
  label: string;
  entityType: string;
  columns: ViewDef['columns'];
  filters?: Record<string, unknown>;
  sort?: Array<{ id: string; desc: boolean }>;
  isShared?: boolean;
  departmentKey?: string | null;
}

export function useSaveView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveViewInput) => {
      const body = {
        entityTypeCode: input.entityType,
        code: input.code,
        label: input.label,
        columns: input.columns.map((c) => ({ attr_code: c.attrCode, width: c.width, pinned: c.pinned, hidden: c.hidden })),
        filters: input.filters ?? {},
        sort: input.sort ?? [],
        isShared: input.isShared ?? false,
        reason: input.isShared ? `Shared registry view "${input.label}"` : undefined,
      };
      return input.id
        ? apiFetch<unknown>(endpoints.view(input.id), { method: 'PUT', body, correlationId: newCorrelationId() })
        : apiFetch<unknown>(endpoints.createView, { method: 'POST', body, correlationId: newCorrelationId() });
    },
    onSuccess: (_d, input) => {
      void qc.invalidateQueries({ queryKey: qk.views(input.entityType) });
    },
  });
}

export interface UpdateEntityInput {
  entityId: string;
  name?: string;
  /** null or "" clears it. */
  description?: string | null;
  reason?: string;
}

/**
 * #287: identity fields after create — `PATCH /entities/{id}`, the route MCP `update_entity`
 * uses. The server writes the `entity.update` change_log line (before/after, reason).
 */
export function useUpdateEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ entityId, reason, ...fields }: UpdateEntityInput) =>
      apiFetch<{ id: string; changed: boolean }>(endpoints.entity(entityId), {
        method: 'PATCH',
        correlationId: newCorrelationId(),
        body: { ...fields, ...(reason?.trim() ? { reason: reason.trim() } : {}) },
      }),
    onSettled: (_data, _err, input) => {
      void qc.invalidateQueries({ queryKey: ['cigs', 'registry'] });
      void qc.invalidateQueries({ queryKey: qk.entity(input.entityId) });
      void qc.invalidateQueries({ queryKey: qk.history(input.entityId) });
    },
  });
}
